'use client';

import React, { useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from '../context/UserContext';
import HeroSection from './course-details/HeroSection/HeroSection';
import CourseContentLayout from './course-details/CourseContentLayout';
import AddSectionButton from './course-details/AdminControls/AddSectionButton';
import LessonModal from './course-details/Modals/LessonModal';
import EditModal from './course-details/Modals/EditModal';
import SyllabusSection from './course-details/syllabus/SyllabusSection';
import AvailableSections from './AvailableSections';
import CancelledSections from './CancelledSections';
import ReviewsSection from './course-details/reviews/ReviewsSection';
import Footer from './footer/Footer';
import WhatsIncluded from './course-details/WhatsIncluded';
import LearningGoals from './course-details/LearningGoals';
import Sidebar from './course-details/Sidebar';
import WhatsIncludedModal from './course-details/Modals/WhatsIncludedModal';
import LearningGoalsModal from './course-details/Modals/LearningGoalsModal';
import SidebarModal from './course-details/Modals/SidebarModal';
import HeroSectionModal from './course-details/Modals/HeroSectionModal';
import AddSectionModal from './course-details/Modals/AddSectionModal';
import { canManageSections } from '@/utils/permissions';
import AboutTeacher from './course-details/AboutTeacher/AboutTeacher';
import styles from '../styles/courseDetails.module.css';

export default function CourseDetails({
  course,
  whatsIncludedContent = {},
  learningGoalsContent = '',
  sidebarContent = {},
  lessons = [],
  initialReviews = [],
  totalReviewCount = 0,
  canLeaveReview = false,
  existingReview = null,
  teacherAvatarUrl = '/images/demo-profile.png',
}) {
  const user = useContext(UserContext);
  const [courseContent, setCourseContent] = useState(course);
  const [lessonsList, setLessons] = useState(lessons);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSection] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [lessonModalOpen, setLessonModalOpen] = useState(false);
  const [editingLessonId, setEditingLessonId] = useState(null);
  const [lessonName, setLessonName] = useState('');
  const [lessonDescription, setLessonDescription] = useState('');
  const [whatsIncludedModalOpen, setWhatsIncludedModalOpen] = useState(false);
  const [whatsIncludedContentState, setWhatsIncludedContent] =
    useState(whatsIncludedContent);
  const [learningGoalsModalOpen, setLearningGoalsModalOpen] = useState(false);
  const [learningGoalsContentState, setLearningGoalsContent] =
    useState(learningGoalsContent);
  const [sidebarModalOpen, setSidebarModalOpen] = useState(false);
  const [sidebarContentState, setSidebarContent] = useState({
    ...sidebarContent,
    completionCount: course?.completionCount || 0,
    ages: course?.ages,
  });
  const [heroSectionModalOpen, setHeroSectionModalOpen] = useState(false);
  const [addSectionModalOpen, setAddSectionModalOpen] = useState(false);

  const offeredSectionsRef = useRef(null);

  useEffect(() => {
    setSidebarContent((prev) => ({
      ...prev,
      ages: courseContent?.ages,
      completionCount: course?.completionCount || 0,
    }));
  }, [courseContent, course?.completionCount]);

  const handleSeeAvailableTimes = () => {
    const el = offeredSectionsRef.current;

    if (!el || typeof window === 'undefined') return;

    const isPhone =
      (window.matchMedia?.('(max-width: 767px)')?.matches ?? false) ||
      window.innerWidth < 768;

    if (!isPhone) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    const headerOffset = 180; 

    window.setTimeout(() => {
      const rect = el.getBoundingClientRect();
      const targetY = window.scrollY + rect.top - headerOffset;

      window.scrollTo({
        top: Math.max(targetY, 0),
        behavior: 'smooth',
      });
 
    }, 120);
  };

  const handleSaveHeroSection = async (
    name,
    description,
    imageUrl,
    filePath,
    introVideoUrl
  ) => {
    try {
      const x = { name, description };
      if (imageUrl != null) {
        x.image = imageUrl;
        x.filePath = filePath;
      }
      if (introVideoUrl != null) {
        x.introVidUrl = introVideoUrl;
      }
      const response = await fetch(
        `/api/editable-content/hero-section/${courseContent.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(x),
          credentials: 'include',
        }
      );

      if (!response.ok) throw new Error(await response.text());

      setHeroSectionModalOpen(false);
      reloadCourse();
      alert(`Hero section updated successfully`);
    } catch (error) {
      console.error('Error saving hero section', error);
      alert('Failed to save hero section of course');
    }
  };

  const handleSaveWhatsIncluded = async (
    title1,
    content1,
    title2,
    content2,
    title3,
    content3,
    title4,
    content4
  ) => {
    try {
      const response = await fetch(
        `/api/editable-content/whats-included/${courseContent.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title1,
            content1,
            title2,
            content2,
            title3,
            content3,
            title4,
            content4,
          }),
          credentials: 'include',
        }
      );

      if (!response.ok) throw new Error(await response.text());

      setWhatsIncludedModalOpen(false);
      reloadWhatsIncluded();
      alert(`'What's Included' section updated successfully`);
    } catch (error) {
      console.error(`Error saving 'What's Included' section:`, error);
      alert(`Failed to save 'What's Included' section`);
    }
  };

  const handleSaveLearningGoals = async () => {
    try {
      const response = await fetch(
        `/api/editable-content/learning-goals/${courseContent.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: learningGoalsContentState }),
          credentials: 'include',
        }
      );

      if (!response.ok) throw new Error(await response.text());

      setLearningGoalsModalOpen(false);
      reloadLearningGoals();
      alert(`Learning goals updated successfully`);
    } catch (error) {
      console.error('Error saving learning goals:', error);
      alert('Failed to save learning goals');
    }
  };

  const handleSaveLesson = async () => {
    const isEdit = !!editingLessonId;
    try {
      const response = await fetch(
        isEdit
          ? `/api/editable-content/syllabus/lessons/${courseContent.id}/${editingLessonId}`
          : `/api/editable-content/syllabus/lessons/course/${courseContent.id}`,
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lessonName,
            description: lessonDescription,
          }),
          credentials: 'include',
        }
      );

      if (!response.ok) throw new Error(await response.text());

      setLessonModalOpen(false);
      reloadLessons();
      alert(`Lesson ${isEdit ? 'updated' : 'added'} successfully`);
    } catch (error) {
      console.error('Error saving lesson:', error);
      alert('Failed to save lesson');
    }
  };

  const handleSaveSidebar = async (
    totalPrice,
    weeklyPrice,
    singleClassDuration,
    courseDuration,
    numLearners,
    ages
  ) => {
    try {
      const response = await fetch(
        `/api/editable-content/sidebar/${courseContent.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            totalPrice,
            weeklyPrice,
            singleClassDuration,
            courseDuration,
            numLearners,
            ages,
          }),
          credentials: 'include',
        }
      );

      if (!response.ok) throw new Error(await response.text());

      setSidebarModalOpen(false);
      reloadCourse();
      reloadSidebar();
      alert(`Sidebar updated successfully`);
    } catch (error) {
      console.error(`Error saving sidebar:`, error);
      alert(`Failed to save sidebar`);
    }
  };

  const reloadCourse = async () => {
    const res = await fetch(`/api/catalog/${courseContent.id}`, {
      method: 'GET',
    });
    const data = await res.json();
    setCourseContent(data);
  };

  const reloadWhatsIncluded = async () => {
    const res = await fetch(
      `/api/editable-content/whats-included/${courseContent.id}`,
      { method: 'GET' }
    );
    const data = await res.json();
    setWhatsIncludedContent(data);
  };

  const reloadLearningGoals = async () => {
    const res = await fetch(
      `/api/editable-content/learning-goals/${courseContent.id}`,
      { method: 'GET' }
    );
    const data = await res.json();
    setLearningGoalsContent(data.content);
  };

  const reloadLessons = async () => {
    const res = await fetch(
      `/api/editable-content/syllabus/lessons/course/${courseContent.id}`
    );
    const data = await res.json();
    setLessons(Array.isArray(data) ? data : []);
  };

  const reloadSidebar = async () => {
    const res = await fetch(
      `/api/editable-content/sidebar/${courseContent.id}`,
      { method: 'GET' }
    );
    const data = await res.json();
    data.completionCount = courseContent.completionCount;
    data.ages = courseContent.ages;
    setSidebarContent(data);
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!confirm('Delete this lesson?')) return;
    try {
      await fetch(
        `/api/editable-content/syllabus/lessons/${courseContent.id}/${lessonId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );
      setLessons((prev) => prev.filter((l) => l.id !== lessonId));
      alert('Lesson deleted');
    } catch (error) {
      console.error('Delete error:', error);
      alert('Delete failed');
    }
  };

  const handleSaveEdit = async () => {
    try {
      const res = await fetch(
        `/api/editable-content/${editSection}/${courseContent.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: editContent }),
          credentials: 'include',
        }
      );
      if (!res.ok) throw new Error('Update failed');

      setEditModalOpen(false);
      alert('Content updated');
    } catch (error) {
      console.error('Update error:', error);
      alert('Update failed');
    }
  };
  const handleCreateSection = async (sectionData) => {
    try {
      const res = await fetch(`/api/classes/${courseContent.id}/sections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sectionData),
        credentials: 'include',
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to create section');
      }

      const data = await res.json();
      alert(data.message || 'Section created successfully');
    } catch (error) {
      console.error('Section creation error:', error);
      throw error;
    }
  };

  if (!course) {
    return (
      <div className="container">
        <p>Course not found</p>
      </div>
    );
  }

  return (
    <>
      <HeroSection
        course={courseContent}
        user={user}
        teacher={courseContent.teacher}
        teacherAvatarUrl={teacherAvatarUrl}
        onEditHeroSection={() => setHeroSectionModalOpen(true)}
      />

      {/* Sticky enroll bar UNDER the hero */}
      <div className={styles.stickyEnrollWrap}>
        <div className={styles.stickyEnrollInner}>
          <Sidebar
            teacher={courseContent.teacher}
            user={user}
            data={sidebarContentState}
            onEditSidebar={() => setSidebarModalOpen(true)}
            onSeeAvailableTimes={handleSeeAvailableTimes}
            onAskQuestion={() => {}}
          />
        </div>
      </div>

      <CourseContentLayout
        leftContent={
          <>
            <div id="courseContentStart" />
            <WhatsIncluded
              teacher={courseContent.teacher}
              user={user}
              data={whatsIncludedContentState}
              onEditWhatsIncluded={() => setWhatsIncludedModalOpen(true)}
            />

            <LearningGoals
              teacher={courseContent.teacher}
              user={user}
              content={learningGoalsContentState}
              onEditLearningGoals={() => setLearningGoalsModalOpen(true)}
            />

            <div className={styles.aboutTeacherInline}>
              <AboutTeacher
                teacher={courseContent.teacher}
                teacherAvatarUrl={teacherAvatarUrl}
              />
            </div>

            <SyllabusSection
              teacher={courseContent.teacher}
              lessons={lessonsList}
              user={user}
              onAddLesson={() => {
                setEditingLessonId(null);
                setLessonName('');
                setLessonDescription('');
                setLessonModalOpen(true);
              }}
              onEditLesson={(id) => {
                const lesson = lessonsList.find((l) => l.id === id);
                setLessonName(lesson.lessonName);
                setLessonDescription(lesson.description);
                setEditingLessonId(id);
                setLessonModalOpen(true);
              }}
              onDeleteLesson={handleDeleteLesson}
            />

            {/* IMPORTANT: add class for scroll offset so sticky bar doesn't cover it */}
            <section
              ref={offeredSectionsRef}
              className={styles.offeredSectionsAnchor}
            >
              <AvailableSections courseId={courseContent.id} course={course} />
            </section>

            {['ADMIN', 'SUPER_ADMIN'].includes(user?.role) && (
              <CancelledSections courseId={courseContent.id} />
            )}

            {canManageSections(user, courseContent) && (
              <AddSectionButton onClick={() => setAddSectionModalOpen(true)} />
            )}

            <ReviewsSection
              courseId={courseContent.id}
              initialReviews={initialReviews}
              totalReviewCount={totalReviewCount}
              canLeaveReview={canLeaveReview}
              existingReview={existingReview}
            />
          </>
        }
        sidebar={null}
      />

      <HeroSectionModal
        isOpen={heroSectionModalOpen}
        onClose={() => setHeroSectionModalOpen(false)}
        courseContent={courseContent}
        onSave={handleSaveHeroSection}
      />

      <WhatsIncludedModal
        isOpen={whatsIncludedModalOpen}
        onClose={() => setWhatsIncludedModalOpen(false)}
        whatsIncludedContent={whatsIncludedContentState}
        onSave={handleSaveWhatsIncluded}
      />

      <LearningGoalsModal
        isOpen={learningGoalsModalOpen}
        onClose={() => setLearningGoalsModalOpen(false)}
        learningGoalsContent={learningGoalsContentState}
        setLearningGoalsContent={setLearningGoalsContent}
        onSave={handleSaveLearningGoals}
      />

      <SidebarModal
        isOpen={sidebarModalOpen}
        onClose={() => setSidebarModalOpen(false)}
        sidebarContent={sidebarContentState}
        onSave={handleSaveSidebar}
      />

      <LessonModal
        isOpen={lessonModalOpen}
        onClose={() => setLessonModalOpen(false)}
        lessonName={lessonName}
        setLessonName={setLessonName}
        lessonDescription={lessonDescription}
        setLessonDescription={setLessonDescription}
        onSave={handleSaveLesson}
        isEditing={!!editingLessonId}
      />

      <EditModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        editContent={editContent}
        setEditContent={setEditContent}
        onSave={handleSaveEdit}
        sectionName={editSection}
      />

      <AddSectionModal
        isOpen={addSectionModalOpen}
        onClose={() => setAddSectionModalOpen(false)}
        onSave={handleCreateSection}
        courseId={courseContent.id}
        userRole={user?.role}
        sidebarHints={{
          weeklyFrequency: sidebarContentState?.weeklyFrequency || 1,
          courseDurationWeeks: sidebarContentState?.courseDuration || 10,
          singleClassDurationMinutes:
            sidebarContentState?.singleClassDuration || 60,
        }}
      />

      <Footer />
    </>
  );
}
